import React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/db.ts';
import { logger } from '../services/logger.ts';
import { CheckCircle } from 'lucide-react';

export function AuthVerify() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'verified' | 'failed'>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    // Log URL and hash for debugging
    console.log('AuthVerify page loaded:', {
      url: window.location.href,
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash
    });

    // Give the Supabase client a moment to handle the session from the URL fragment
    const timer = setTimeout(async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          logger.error('Error getting session after verification:', sessionError);
          throw new Error('Could not retrieve session. Please try logging in.');
        }

        if (session) {
          // User is verified AND logged in (best case scenario)
          logger.info('Verification successful with active session:', { 
            userId: session.user.id,
            email: session.user.email
          });

          setStatus('verified');

          // Redirect to markets page since user is logged in
          setTimeout(() => {
            navigate('/markets', { 
              replace: true,
              state: { message: 'Welcome! Your email has been verified.' }
            });
          }, 2000);
          
        } else {
          // No active session, but verification may have succeeded
          // Check URL parameters to see if we came from a successful verification
          const urlParams = new URLSearchParams(window.location.search);
          const hasVerificationParams = urlParams.has('access_token') || urlParams.has('refresh_token') || window.location.hash.includes('access_token');
          
          if (hasVerificationParams || window.location.pathname === '/auth/verify') {
            // Likely successful verification without auto-login
                         logger.info('Email verification completed successfully (no auto-login)');
             setStatus('verified');

            // Redirect to login with success message
            setTimeout(() => {
              navigate('/auth/login', { 
                replace: true,
                state: { message: 'Email verified successfully! You can now log in.' }
              });
            }, 2000);
          } else {
            // No session and no verification indicators
            logger.warn('No session found and no verification indicators');
            throw new Error('Verification status unclear. Please try logging in.');
          }
        }

      } catch (err) {
        const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
        logger.error('Error in AuthVerify component:', message);
        setErrorMessage(message);
        setStatus('failed');
        
        // Redirect to login with an error message
        setTimeout(() => {
          navigate('/auth/login', { 
            replace: true,
            state: { error: 'Verification completed, but please try logging in.' }
          });
        }, 3000);
      }
    }, 1000); // 1-second delay to allow Supabase client to process URL

    return () => clearTimeout(timer); // Cleanup the timer
  }, [navigate]);

  if (status === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-64px)] bg-gray-100 px-4">
        <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-wayja-teal mx-auto mb-4"></div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Finalizing Verification</h1>
          <p className="text-gray-600">Please wait a moment...</p>
        </div>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-64px)] bg-gray-100 px-4">
        <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md text-center">
           <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Verification Failed</h1>
          <p className="text-gray-600 mb-4">{errorMessage}</p>
          <p className="text-sm text-gray-500">Redirecting you to login...</p>
        </div>
      </div>
    );
  }

  // status === 'verified'
  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-64px)] bg-gray-100 px-4">
      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md text-center">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Email Verified!</h1>
        <p className="text-gray-600 mb-4">Welcome to Wayja! You are now logged in.</p>
        <p className="text-sm text-gray-500">Redirecting you to the markets...</p>
      </div>
    </div>
  );
} 